/**
 * `<SearchSurface>` — the Search surface root.
 *
 * 2026-06-01 (Chris ask): stripped to "super clean — just the search bar and a time
 * window filter." Removed: left-hand `<FacetFilterPanel>` sidebar, the page-head
 * density + grouping controls, the `<AppliedFilterChips>` strip, the results-head
 * technical-mode label, and (second pass) the entire `<SearchEmptyState>`'s
 * workflow-starters grid + corpus-snapshot KPI tiles + saved-searches + recent-
 * queries blocks. What remains is a single-column layout: page-head (title + sub
 * only) → search bar → preset time-window chips → results (or the slim idle block).
 *
 * U7 (design review 2026-07-05): results are partitioned by a relevance floor
 * (`resultRelevance.ts` — weak tail folds behind a disclosure; honest exact
 * count in the results head), and the idle canvas earns its space again with
 * a SLIM `<SearchEmptyState>` (saved searches if any + one mode-explainer
 * line) — not the old guided console.
 *
 * STATE MODEL (frontend.md §2.2): query + mode + filters.dateFrom remain URL state
 * via `useSearchParams` / `searchUrlState`'s codec. Density + grouping are still
 * in the URL codec (saved searches encode them) but the UI surfaces no toggles —
 * the defaults from `parseSearchUrl` apply. The detail drawer's open note is
 * ephemeral (`useSearchViewStore`). Result data comes from `useSearchQuery`.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSearchQuery, toErrorEnvelope } from '@/hooks';
import { useSearchViewStore } from '@/stores';
// Leaf-module imports (NOT the '@/shell' barrel): the barrel pulls in AppShell →
// surfaceRegistry → SearchSurface, so importing it from here would close an ESM
// cycle. PageHead/PageBody import nothing from the shell — acyclic.
import { PageHead } from '@/shell/PageHead';
import { PageBody } from '@/shell/PageBody';
import { LoadingState, EmptyState, InlineError } from '@/components';
import type { Filters, ResultItem, SavedSearch } from '@/types';
import type { SearchMode } from '@/hooks';
import { SearchBar } from './SearchBar';
import { SearchEmptyState } from './SearchEmptyState';
import { VirtualizedResultList } from './VirtualizedResultList';
import { ResultDetailDrawer } from './ResultDetailDrawer';
import {
  parseSearchUrl,
  serializeSearchUrl,
  type SearchUrlState,
} from './searchUrlState';
import {
  formatAllWeakHeader,
  formatMatchCount,
  partitionByRelevance,
} from './resultRelevance';
import './searchSurface.css';

/** Preset time-window chips. `null` days = "all" (clear both dateFrom + dateTo). */
const TIME_PRESETS: { id: string; label: string; days: number | null }[] = [
  { id: '7d', label: '7d', days: 7 },
  { id: '30d', label: '30d', days: 30 },
  { id: '90d', label: '90d', days: 90 },
  { id: 'ytd', label: 'YTD', days: -1 }, // special — Jan 1 of current year
  { id: 'all', label: 'all', days: null },
];

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Resolve which preset chip matches the current `filters.dateFrom` (or null if
 * the value was authored by hand and doesn't match any preset).
 */
function activePresetId(filters: Filters): string | null {
  if (filters.dateFrom === undefined && filters.dateTo === undefined) return 'all';
  if (filters.dateTo !== undefined) return null;
  if (filters.dateFrom === undefined) return null;
  // YTD = Jan 1 of the current year.
  const jan1 = new Date(new Date().getFullYear(), 0, 1);
  if (filters.dateFrom === toIsoDate(jan1)) return 'ytd';
  // Numeric presets — match by days-back-from-today.
  const from = new Date(filters.dateFrom);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  from.setHours(0, 0, 0, 0);
  const days = Math.round((today.getTime() - from.getTime()) / (24 * 3600 * 1000));
  const match = TIME_PRESETS.find((p) => p.days === days);
  return match?.id ?? null;
}

export function SearchSurface(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();

  // The URL is the source of truth for the surface state (frontend.md §2.2).
  const urlState = useMemo<SearchUrlState>(
    () => parseSearchUrl(searchParams),
    [searchParams],
  );

  const detailDrawerNoteId = useSearchViewStore((s) => s.detailDrawerNoteId);
  const openDetailDrawer = useSearchViewStore((s) => s.openDetailDrawer);
  const closeDetailDrawer = useSearchViewStore((s) => s.closeDetailDrawer);

  // Write a new surface state back into the URL — this is what makes a saved search a URL.
  const writeUrl = useCallback(
    (next: SearchUrlState) => {
      setSearchParams(serializeSearchUrl(next), { replace: false });
    },
    [setSearchParams],
  );

  const onSubmitQuery = useCallback(
    (query: string) => writeUrl({ ...urlState, query }),
    [urlState, writeUrl],
  );
  const onModeChange = useCallback(
    (mode: SearchMode) => writeUrl({ ...urlState, mode }),
    [urlState, writeUrl],
  );
  const onPresetClick = useCallback(
    (preset: (typeof TIME_PRESETS)[number]) => {
      const nextFilters: Filters = { ...urlState.filters };
      if (preset.days === null) {
        // "all" — clear both bounds.
        nextFilters.dateFrom = undefined;
        nextFilters.dateTo = undefined;
      } else if (preset.id === 'ytd') {
        const jan1 = new Date(new Date().getFullYear(), 0, 1);
        nextFilters.dateFrom = toIsoDate(jan1);
        nextFilters.dateTo = undefined;
      } else {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - (preset.days as number));
        nextFilters.dateFrom = toIsoDate(cutoff);
        nextFilters.dateTo = undefined;
      }
      writeUrl({ ...urlState, filters: nextFilters });
    },
    [urlState, writeUrl],
  );

  const { query, items, truncated, warnings, totalEstimate } = useSearchQuery({
    query: urlState.query,
    mode: urlState.mode,
    filters: urlState.filters,
  });

  const onOpenDetail = useCallback(
    (item: ResultItem) => openDetailDrawer(item.noteId),
    [openDetailDrawer],
  );

  // Saved-search jump (U7 idle wiring): the stored query object is the
  // structured retrieval request { query, filters, profile } — pull the text
  // and run it through the normal URL write.
  const onJumpToSaved = useCallback(
    (search: SavedSearch) => {
      const q = typeof search.query['query'] === 'string' ? (search.query['query'] as string) : '';
      writeUrl({ ...urlState, query: q });
    },
    [urlState, writeUrl],
  );

  const hasQuery = urlState.query.trim().length > 0;
  const showResults = hasQuery && query.isSuccess && items.length > 0;
  const activePreset = activePresetId(urlState.filters);

  // U7 relevance floor (design review P1 item 1): fold below-floor results
  // behind a disclosure; when the WHOLE set is weak, present it honestly.
  const partition = useMemo(() => partitionByRelevance(items), [items]);

  const matchCountLabel =
    hasQuery && query.isSuccess && items.length > 0
      ? partition.allWeak
        ? formatAllWeakHeader(partition.weak.length)
        : formatMatchCount({
            loaded: items.length,
            strong: partition.strong.length,
            weak: partition.weak.length,
            totalEstimate,
            hasMore: query.hasNextPage ?? false,
          })
      : null;

  return (
    // Idle (no query yet) → vertically center the search bar on the otherwise-blank page.
    <div className={`surface surface--search${hasQuery ? '' : ' surface--search--idle'}`}>
      {/* --- page-head: eyebrow + title. No subtitle, no action controls. ---------- */}
      <PageHead eyebrow="retrieval" title="Search" />

      {/* --- page-body: single column. Search bar + time-window chips + results. --
          `.se-page` overrides `.page-body`'s default padding (same specificity,
          later in app.css), so the rendered layout is unchanged. */}
      <PageBody className="se-page">
        <div className="se-results">
          <SearchBar
            query={urlState.query}
            mode={urlState.mode}
            onSubmit={onSubmitQuery}
            onModeChange={onModeChange}
          />

          {/* Time-window preset chips — the one filter that stays. */}
          <div className="se-time-window" role="group" aria-label="Time window">
            {TIME_PRESETS.map((p) => {
              const isActive = activePreset === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  className={`se-time-window__chip${isActive ? ' se-time-window__chip--active' : ''}`}
                  aria-pressed={isActive}
                  onClick={() => onPresetClick(p)}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          {hasQuery && matchCountLabel !== null && (
            <div className="results-head">
              <span className="results-head__sum">
                <strong>{matchCountLabel}</strong>
              </span>
            </div>
          )}

          {/* U7 idle view (design review P3 item 8): the 2026-06-01 "renders
              nothing" state left the centered canvas empty. Now: saved
              searches (only if any exist) + one quiet mode-explainer line.
              The centered-launcher composition is unchanged. */}
          {!hasQuery && <SearchEmptyState onJump={onJumpToSaved} />}

          {hasQuery && query.isLoading && <LoadingState label="Searching…" />}

          {hasQuery && query.isError && (
            <InlineError
              error={toErrorEnvelope(query.error)}
              onRetry={() => void query.refetch()}
            />
          )}

          {hasQuery && query.isSuccess && items.length === 0 && (
            <EmptyState
              title="No results"
              detail="If the corpus was just ingested, extraction may still be in progress — check Corpus Health for the Stage A queue."
            />
          )}

          {/* Keyed by query+mode so the weak-fold disclosure (and scroll)
              reset on a new search rather than leaking across queries. When
              ALL results are weak they render as the main list (with the
              honest header above) instead of hiding behind a fold. */}
          {showResults && (
            <VirtualizedResultList
              key={`${urlState.mode}:${urlState.query}`}
              items={partition.allWeak ? partition.weak : partition.strong}
              weakItems={partition.allWeak ? [] : partition.weak}
              truncated={truncated}
              warnings={warnings}
              isFetchingNextPage={query.isFetchingNextPage}
              hasNextPage={query.hasNextPage ?? false}
              onLoadMore={() => void query.fetchNextPage()}
              onOpenDetail={onOpenDetail}
              density={urlState.density}
            />
          )}
        </div>
      </PageBody>

      <ResultDetailDrawer noteId={detailDrawerNoteId} onClose={closeDetailDrawer} />
    </div>
  );
}
